import type * as Api from '@ViewModels';
import { format } from 'date-fns';
import type { PhoneCallView } from '../../../queries/PhoneCall/usePhoneCallFilterQuery';
import {
	type CoachingAdherenceRepScore,
	MIN_CALLS_FOR_SHARE,
	type Tier,
	partlyOrBetterShare,
} from '../../constants/coachingAdherence';

export const MAX_RANGE_DAYS = 31;

/** The board value the API reserves for every rep in the viewer's scope. */
export const ALL_REPS_BOARD = '@all';

const MS_PER_DAY = 86_400_000;

/** The team's working bar for a compliant rep: at least partly followed on this share of calls past the hook. */
export const COMPLIANT_SHARE = 0.6;

// A partly-followed call still used the coach, so it clears the flag too.
export const isNeverFollower = (rep: CoachingAdherenceRepScore): boolean =>
	rep.pastHookCalls > 0 && rep.followedCalls === 0 && (rep.partlyFollowedCalls ?? 0) === 0;

export interface ScoreboardSummary {
	/** Reps with enough calls past the hook to judge. */
	rankedReps: number;
	/** Ranked reps who used the coach at least partly on {@link COMPLIANT_SHARE} or more of their calls past the hook. */
	compliantReps: number;
	neverFollowed: number;
	pastHookCalls: number;
	cohortShare: number | null;
	/** Calls past the hook scored at least half the followed score, followed calls included; null when the API predates the count. */
	partlyShare: number | null;
	demosPerFollowedCall: number | null;
	demosPerPastHookCall: number | null;
	/** Dialing totals and their per-demo rates; null when the API predates them. A rate is null with no demos. */
	activity: {
		/** Every dial, confirmation and reschedule dials included; `dials` is prospecting dials only. */
		allDials: number;
		dials: number;
		connects: number;
		demos: number;
		dialsPerDemo: number | null;
		connectsPerDemo: number | null;
	} | null;
	/** Reps in auto mode out of all reps; null when the API predates the setting. */
	autoReps: number | null;
	totalReps: number;
}

const ratio = (numerator: number, denominator: number): number | null =>
	denominator > 0 ? numerator / denominator : null;

export function summarizeScoreboard(reps: CoachingAdherenceRepScore[]): ScoreboardSummary {
	const ranked = reps.filter(rep => rep.pastHookCalls >= MIN_CALLS_FOR_SHARE);
	const sum = (pick: (rep: CoachingAdherenceRepScore) => number) => reps.reduce((total, rep) => total + pick(rep), 0);
	const pastHook = sum(rep => rep.pastHookCalls);
	const followed = sum(rep => rep.followedCalls);

	return {
		rankedReps: ranked.length,
		compliantReps: ranked.filter(rep => (partlyOrBetterShare(rep) ?? 0) >= COMPLIANT_SHARE).length,
		neverFollowed: ranked.filter(isNeverFollower).length,
		pastHookCalls: pastHook,
		cohortShare: ratio(followed, pastHook),
		partlyShare: reps.some(rep => rep.partlyFollowedCalls != null)
			? ratio(followed + sum(rep => rep.partlyFollowedCalls ?? 0), pastHook)
			: null,
		demosPerFollowedCall: ratio(
			sum(rep => rep.bookedFollowedCalls),
			followed
		),
		demosPerPastHookCall: ratio(
			sum(rep => rep.bookedCalls),
			pastHook
		),
		activity: sendsRepDetails(reps) ? summarizeActivity(reps) : null,
		autoReps: sendsRepDetails(reps) ? reps.filter(isAutoMode).length : null,
		totalReps: reps.length,
	};
}

function summarizeActivity(reps: CoachingAdherenceRepScore[]) {
	const sum = (pick: (rep: CoachingAdherenceRepScore) => number | undefined) =>
		reps.reduce((total, rep) => total + (pick(rep) ?? 0), 0);
	const allDials = sum(rep => rep.dials);
	const dials = sum(rep => rep.prospectingDials);
	const connects = sum(rep => rep.connects);
	const demos = sum(rep => rep.demos);
	return {
		allDials,
		dials,
		connects,
		demos,
		dialsPerDemo: ratio(dials, demos),
		connectsPerDemo: ratio(connects, demos),
	};
}

/** One rep's dials and connects per demo; null when the API predates them or the rep booked nothing. */
export function repActivityRates(rep: CoachingAdherenceRepScore) {
	if (rep.prospectingDials == null) {
		return null;
	}
	return summarizeActivity([rep]);
}

/** Per-demo rates read best whole once they're large and to a tenth while small. */
export const formatPerDemo = (value: number | null): string =>
	value == null ? '–' : value >= 100 ? `${Math.round(value)}` : value.toFixed(1);

/** The live coach's mode is a per-rep setting that defaults to off, so only an explicit true is auto mode. */
export const isAutoMode = (rep: CoachingAdherenceRepScore): boolean => rep.autoSuggestions === true;

/**
 * Whether the API sends each rep's dialing totals and coach mode. The totals are always numbers when sent, so they mark
 * the version; the mode alone can't, since a rep who never set it has none.
 */
export const sendsRepDetails = (reps: CoachingAdherenceRepScore[]): boolean =>
	reps.some(rep => rep.prospectingDials != null);

export type CoachStage = 'hook' | 'opener' | 'ask';

const STAGE_COUNTS: Record<CoachStage, (rep: CoachingAdherenceRepScore) => [number, number]> = {
	hook: rep => [rep.hookUsedCalls ?? 0, rep.hookCalls ?? 0],
	opener: rep => [rep.openerUsedCalls ?? 0, rep.openerCalls ?? 0],
	ask: rep => [rep.askUsedCalls ?? 0, rep.askCalls ?? 0],
};

export const stageCounts = (rep: CoachingAdherenceRepScore, stage: CoachStage): { used: number; counted: number } => {
	const [used, counted] = STAGE_COUNTS[stage](rep);
	return { used, counted };
};

/** Pooled share of calls where the rep took the coach's hook, opener or ask; null when no call could show it. */
export function summarizeStageUse(reps: CoachingAdherenceRepScore[]): Record<CoachStage, number | null> {
	const share = (stage: CoachStage) => {
		const totals = reps.map(rep => stageCounts(rep, stage));
		return ratio(
			totals.reduce((sum, t) => sum + t.used, 0),
			totals.reduce((sum, t) => sum + t.counted, 0)
		);
	};
	return { hook: share('hook'), opener: share('opener'), ask: share('ask') };
}

/** '' or an empty list means any; a list matches a rep in any of its values. */
export interface RepGroupFilters {
	adherenceGroups: string[];
	managerUserId: string;
	quotaTeam: string;
	teams: string[];
}

export const NO_GROUP_FILTERS: RepGroupFilters = {
	adherenceGroups: [],
	managerUserId: '',
	quotaTeam: '',
	teams: [],
};

export interface GroupFilterOption {
	value: string;
	label: string;
}

const distinctOptions = (pairs: Array<[string | null | undefined, string | null | undefined]>): GroupFilterOption[] => {
	const byValue = new Map<string, string>();
	for (const [value, label] of pairs) {
		if (value && !byValue.has(value)) {
			byValue.set(value, label || value);
		}
	}
	return [...byValue].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
};

export function groupFilterOptions(reps: CoachingAdherenceRepScore[]) {
	return {
		adherenceGroups: distinctOptions(reps.map(rep => [rep.adherenceGroup, rep.adherenceGroup])),
		managers: distinctOptions(reps.map(rep => [rep.managerUserId, rep.managerName])),
		quotaTeams: distinctOptions(reps.map(rep => [rep.quotaTeam, rep.quotaTeam])),
		teams: distinctOptions(reps.flatMap(rep => (rep.teams ?? []).map(team => [team, team] as [string, string]))),
	};
}

export const applyGroupFilters = (
	reps: CoachingAdherenceRepScore[],
	filters: RepGroupFilters
): CoachingAdherenceRepScore[] =>
	reps.filter(
		rep =>
			(filters.adherenceGroups.length === 0 || filters.adherenceGroups.includes(rep.adherenceGroup ?? '')) &&
			(!filters.managerUserId || rep.managerUserId === filters.managerUserId) &&
			(!filters.quotaTeam || rep.quotaTeam === filters.quotaTeam) &&
			(filters.teams.length === 0 || (rep.teams ?? []).some(team => filters.teams.includes(team)))
	);

export const formatRate = (value: number | null): string => (value == null ? 'n/a' : `${(value * 100).toFixed(1)}%`);

export const todayDateString = (now: Date = new Date()): string => format(now, 'yyyy-MM-dd');

const parseLocalDate = (value: string): Date | null => {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
		return null;
	}
	const date = new Date(`${value}T00:00:00`);
	return Number.isNaN(date.getTime()) ? null : date;
};

export type ScoreboardRange = { start: string; end: string } | { error: string };

/** The UI's end date is inclusive; the API's end is exclusive, so it gets the next local midnight. */
export function buildScoreboardRange(startDate: string, endDate: string): ScoreboardRange {
	const start = parseLocalDate(startDate);
	const lastDay = parseLocalDate(endDate);
	if (!start || !lastDay) {
		return { error: 'Enter a start and end date.' };
	}
	const end = new Date(lastDay);
	end.setDate(end.getDate() + 1);
	if (end <= start) {
		return { error: 'The end date must be on or after the start date.' };
	}
	if (end.getTime() - start.getTime() > MAX_RANGE_DAYS * MS_PER_DAY) {
		return { error: `Pick a range of ${MAX_RANGE_DAYS} days or fewer.` };
	}
	return { start: start.toISOString(), end: end.toISOString() };
}

export interface VerdictDisplay {
	label: string;
	tier: Tier;
}

export function verdictDisplay(verdict: Api.CoachingThreadVerdict): VerdictDisplay {
	switch (verdict) {
		case 'Followed':
		case 'FollowedDifferently':
			return { label: 'Took it', tier: 'good' };
		case 'Partially':
			return { label: 'Partly', tier: 'warn' };
		case 'Missed':
			return { label: 'Skipped', tier: 'bad' };
		case 'WentAgainst':
			return { label: 'Went against it', tier: 'bad' };
		default:
			return { label: 'Not counted', tier: 'neutral' };
	}
}

const TIER_PILL_COLORS = { good: 'green', warn: 'yellow', bad: 'red', neutral: 'gray' } as const satisfies Record<
	Tier,
	string
>;

export const tierPillColor = (tier: Tier) => TIER_PILL_COLORS[tier];

export const scoreTier = (score: number, followedScore: number): Tier => {
	if (score >= followedScore) {
		return 'good';
	}
	return score >= followedScore / 2 ? 'warn' : 'bad';
};

const NOT_SCORED_LABELS: Record<Api.CoachingNotScoredReason, string> = {
	NotConnected: 'Not scored: did not connect',
	NoCoaching: 'Not scored: no coaching shown',
};

export const notScoredLabel = (reason?: Api.CoachingNotScoredReason | null): string =>
	(reason && NOT_SCORED_LABELS[reason]) || 'Not scored';

const OUTCOME_LABELS: Record<string, string> = {
	HookRejected: 'Hook rejected',
	QuickPitch: 'Quick pitch',
	QualifiedPitch: 'Qualified pitch',
	Booked: 'Booked',
	Stumped: 'Stumped',
};

/** Unknown outcomes are split from PascalCase so a new server value still reads as words. */
export const loggedOutcomeLabel = (outcome?: string | null): string | null => {
	if (!outcome) {
		return null;
	}
	return OUTCOME_LABELS[outcome] ?? outcome.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const isJudgedCall = (view: PhoneCallView): boolean => Boolean(view.phoneCall.coachingAdherenceResult?.judgment);

export const isNotScoredCall = (view: PhoneCallView): boolean =>
	Boolean(view.phoneCall.coachingAdherenceResult?.notScoredReason);

/** Calls the judge looked at, judged first; each group keeps the server's newest-first order. */
export const selectJudgeCalls = (calls: PhoneCallView[]): PhoneCallView[] => [
	...calls.filter(isJudgedCall),
	...calls.filter(call => !isJudgedCall(call) && isNotScoredCall(call)),
];
